import React, { useEffect, useState } from "react";
import { useForm, Controller, useFieldArray } from "react-hook-form";
import { get, post } from "../../services/api";
import { toast } from "react-toastify";
import Select from "react-select";
import CustomDatatableGrid from "../../components/CustomDatatableGrid";
import { customTableStyles } from "../../assets/CustomTableStyles";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "jspdf-autotable";
import logo from "../../assets/img/logo-full.png";

function ConcessionPlan() {
  const token = localStorage.getItem("token");
  const centerId = localStorage.getItem("centerId");
  const organizationId = localStorage.getItem("organizationId");

  const [isLoading, setIsLoading] = useState(true);
  const [triggerRerender, setTriggerRerender] = useState(false);

  const {
    register,
    formState: { errors, isSubmitting },
    control,
    handleSubmit,
    watch,
    setValue,
    trigger,
    reset,
  } = useForm({
    defaultValues: {
      id: 0,
      name: "",
      Type: "",
      createdBy: localStorage.getItem("userId"),
      organizationId: localStorage.getItem("organizationId"),
      concessionTypeDetails: [],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "concessionTypeDetails",
  });

  const [feeTypes, setFeeTypes] = useState([]);
  const [concessionPlans, setConcessionPlans] = useState([]);

  useEffect(() => {
    const fetchConcessionPlans = async () => {
      try {
        const response = await get(
          `ConcessionType/GetAllConcessionTypeByOrganizationIdAsync?organizationId=${organizationId}&centerId=${centerId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        setConcessionPlans(response);
      } catch (error) {
        console.error("Error fetching concessionPlans:", error);
      }
    };

    fetchConcessionPlans();
  }, [triggerRerender]);

  useEffect(() => {
    const fetchFeeTypes = async () => {
      try {
        const response = await get(
          `FeeType/GetFeeTypeByOrganizationIdAsync?organizationId=${organizationId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        setFeeTypes(response.feeType);

        const concessionTypeDetailsArray = response.feeType.map((feeType) => ({
          feeTypeId: feeType.id || "",
          concessionPercentage: "",
        }));

        setValue("concessionTypeDetails", concessionTypeDetailsArray);
        setIsLoading(false);
      } catch (error) {
        console.error("Error fetching feeType:", error);
      }
    };

    fetchFeeTypes();
  }, [triggerRerender]);

  const handleEditClick = async (rowId) => {
    const singleConcessionPlan = await get(
      `ConcessionType/GetConcessionTypeByIdAsync?ConcessionTypeId=${rowId}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    // Create a mapping for feeTypeId -> concessionTypeDetails
    const concessionTypeDetailsMap =
      singleConcessionPlan.concessionTypeDetails.reduce((map, detail) => {
        map[detail.feeTypeId] = detail;
        return map;
      }, {});

    // Ensure that concessionTypeDetails matches the order of feeTypes
    const mappedConcessionPlanDetails = feeTypes.map((feeType) => {
      return {
        feeTypeId: feeType.id,
        concessionPercentage:
          concessionTypeDetailsMap[feeType.id]?.concessionPercentage || "",
      };
    });

    // Reset the form with mapped concessionTypeDetails
    reset({
      id: singleConcessionPlan.id || 0,
      name: singleConcessionPlan.name || "",
      type: singleConcessionPlan.type || "",
      createdBy:
        singleConcessionPlan.createdBy || localStorage.getItem("userId"),
      updatedBy:
        singleConcessionPlan.updatedBy || localStorage.getItem("userId"),
      organizationId:
        singleConcessionPlan.organizationId ||
        localStorage.getItem("organizationId"),
      concessionTypeDetails: mappedConcessionPlanDetails, // Use the ordered concessionTypeDetails
    });

    // Clear and re-append rows in the form field array
    remove(); // Clear existing rows
    mappedConcessionPlanDetails.forEach((detail) => {
      append(detail); // Add the correctly mapped rows
    });
  };

  const columns = [
    {
      accessorKey: "mrt-row-numbers",
      header: "Sr No.",
      enableColumnOrdering: false,
      enableEditing: false,
      enableSorting: false,
      size: 80,
      Cell: ({ row }) => row.index + 1,
    },
    {
      header: "Name",
      accessorKey: "name",
      // size: 200,
    },
    {
      header: "Type",
      accessorKey: "type",
      // size: 200,
    },
    {
      id: "actions",
      header: "Action",
      size: 120,
      Cell: ({ row }) => (
        <button
          type="button"
          className="btn p-0"
          onClick={() => handleEditClick(row.original.id)}
        >
          <i className="fas fa-pencil"></i>
        </button>
      ),
    },
  ];

  const exportToExcel = () => {
    const filteredData = concessionPlans.map((row, index) => ({
      "Sr No": index + 1,
      Name: row.name,
      Type: row.type,
    }));

    const worksheet = XLSX.utils.json_to_sheet(filteredData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "ConcessionPlans");
    XLSX.writeFile(workbook, "ConcessionPlans.xlsx");
  };

  const exportToPDF = () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const logoWidth = 30;
    const logoHeight = 8;
    const logoX = (pageWidth - logoWidth) / 2;
    const logoY = 10;
    // doc.addImage(logo, "PNG", logoX, logoY, logoWidth, logoHeight);

    const title = "Concession Plans List";
    const titleFontSize = 12;
    doc.setFontSize(titleFontSize);
    doc.setFont("helvetica", "bold");
    const textWidth = doc.getTextWidth(title);
    const titleX = (pageWidth - textWidth) / 2;
    const titleY = logoY + logoHeight + 10;
    doc.text(title, titleX, titleY);

    const headers = [["Sr No", "Name", "Type"]];

    const bodyData = concessionPlans.map((row, index) => [
      index + 1,
      row.name,
      row.type,
    ]);

    doc.autoTable({
      head: headers,
      body: bodyData,
      theme: "striped",
      startY: titleY + 10,
      headStyles: {
        fillColor: [132, 23, 26],
        textColor: [255, 255, 255],
      },
    });

    doc.save("ConcessionPlans.pdf");
  };

  const saveData = async (data) => {
    if (!token) {
      console.error("Unauthorized.");
      return;
    }

    const updatedConcessionPlanDetail = data.concessionTypeDetails.map(
      (detail) => ({
        ...detail,
        concessionPercentage: detail.concessionPercentage || 0,
      })
    );

    const formData = {
      ...data,
      concessionTypeDetails: updatedConcessionPlanDetail,
      centerId: centerId,
    };

    try {
      const response = await post(
        "ConcessionType/AddUpdateConcessionTypeAsync",
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (response.isSuccess) {
        toast.success(response.message || "Success!");
        setTriggerRerender((prev) => !prev);
        reset({
          id: 0,
          name: "",
          type: "",
          createdBy: localStorage.getItem("userId"),
          organizationId: localStorage.getItem("organizationId"),
          concessionTypeDetails: [],
        });
      }
    } catch (error) {
      console.error("Error.", error);
    }
  };

  const handleChangeOfInputWithLengthCheck = (fieldName) => (e) => {
    const value = e.target.value;
    setValue(fieldName, value, { shouldValidate: true });
  };

  if (isLoading) {
    return <div className="fa fa-spinner fs-7"></div>;
  }

  return (
    <>
      <div className="page-header d-flex mb-2">
        <h2 className="page-heading fs-7">Concession Plan</h2>
      </div>
      <div className="row g-3">
        <div className="col-md-7">
          <div className="card">
            <div className="card-body">
              <form
                className="new-form-style"
                onSubmit={handleSubmit(saveData)}
              >
                <div className="row g-3">
                  <div className="col-md-4">
                    <label className="form-label form-label-new">
                      Name<span className="text-danger fs-9">*</span>
                    </label>
                    <input
                      {...register(`name`, {
                        required: "This field is required.",
                        maxLength: {
                          value: 75,
                          message: "Maximum of 75 characters allowed.",
                        },
                      })}
                      type="text"
                      className="form-control"
                      placeholder=""
                      onChange={handleChangeOfInputWithLengthCheck("name")}
                    />
                    {errors.name && (
                      <small className="text-danger">
                        {errors.name.message}
                      </small>
                    )}
                  </div>
                  <div className="col-md-4">
                    <label className="form-label form-label-new">
                      Type<span className="text-danger fs-9">*</span>
                    </label>
                    <input
                      {...register(`type`, {
                        required: "This field is required.",
                        maxLength: {
                          value: 75,
                          message: "Maximum of 75 characters allowed.",
                        },
                      })}
                      type="text"
                      className="form-control"
                      placeholder=""
                      onChange={handleChangeOfInputWithLengthCheck("type")}
                    />
                    {errors.type && (
                      <small className="text-danger">
                        {errors.type.message}
                      </small>
                    )}
                  </div>
                  <div className="w-100 m-0"></div>
                  <div className="col-md-12">
                    <div className="table-responsive">
                      <table className="table table-bordered custom-table m-0">
                      <thead>
                        <tr>
                          <th>Fee Type</th>
                          <th>Fee Percentage (%)</th>
                          <th>One Time</th>
                          <th>Recurring</th>
                        </tr>
                      </thead>
                      <tbody>
                        {feeTypes?.map((feeType, index) => (
                          <tr key={index}>
                            <td>{feeType.name}</td>
                            <td>
                              <input
                                type="number"
                                className="form-control"
                                // step="0.01"
                                {...register(
                                  `concessionTypeDetails[${index}].concessionPercentage`,
                                  {
                                    value:
                                      fields[index]?.concessionPercentage || "",
                                    maxLength: {
                                      value: 75,
                                      message:
                                        "Maximum of 75 characters allowed.",
                                    },
                                  }
                                )}
                                onInput={(e) => {
                                  if (e.target.value < 0) {
                                    e.target.value = 0;
                                  }
                                }}
                                onChange={handleChangeOfInputWithLengthCheck(
                                  `concessionTypeDetails[${index}].concessionPercentage`
                                )}
                              />
                              {errors.concessionTypeDetails?.[index]
                                ?.concessionPercentage && (
                                <div className="text-danger">
                                  {
                                    errors.concessionTypeDetails[index]
                                      .concessionPercentage.message
                                  }
                                </div>
                              )}
                            </td>
                            <td>
                              <div className="d-flex justify-content-center">
                                <span
                                  className={`text-primary fa ${
                                    feeType.feeFrequency === "One Time"
                                      ? "fa-check"
                                      : ""
                                  }`}
                                ></span>
                              </div>
                            </td>
                            <td>
                              <div className="d-flex justify-content-center">
                                <span
                                  className={`text-primary fa ${
                                    feeType.feeFrequency === "Recurring"
                                      ? "fa-check"
                                      : ""
                                  }`}
                                ></span>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      </table>
                    </div>
                  </div>
                  <div className="col-12">
                    <button
                      className="btn btn-primary btn-sm"
                      type="submit"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? "Save" : "Save"}
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>

        <div className="col-md-5">
          <div className="card">
            <div className="card-body">
              <CustomDatatableGrid
                columns={columns}
                data={concessionPlans}
                onExportExcel={exportToExcel}
                onExportPDF={exportToPDF}
                initialState={{ columnPinning: { right: ["actions"] } }}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default ConcessionPlan;
